import type { AppDispatch } from '../../types';

/**
 * ### DispatchPatchResult
 *
 * @description
 * Shape of the value returned when dispatching RTK Query's updateQueryData or upsertQueryData.
 * undo is optional: updateQueryData returns it; upsertQueryData may omit it when inserting
 * a new cache entry (no previous state to patch).
 */
interface DispatchPatchResult {
  undo?: () => void;
  inversePatches?: unknown[];
  patches?: unknown[];
}

/**
 * ### PatchCollection
 *
 * @description
 * A dispatch result that is known to support rollback (has undo).
 * Used only for patches we store; validated via isUndoablePatch before adding.
 */
interface PatchCollection extends DispatchPatchResult {
  undo: () => void;
}

/**
 * ### isUndoablePatch
 *
 * @description
 * Type guard for dispatch results. Only results with a callable undo are stored and rolled back.
 *
 * @param value - The value returned from dispatching an updateQueryData/upsertQueryData thunk
 * @returns True if value has an undo function and can be tracked for rollback
 */
function isUndoablePatch(value: unknown): value is PatchCollection {
  return value != null && typeof (value as DispatchPatchResult).undo === 'function';
}

/**
 * ### OptimisticUpdateManager
 *
 * @description
 * Manages a collection of optimistic updates and provides rollback functionality
 */
class OptimisticUpdateManager {
  private patches: PatchCollection[] = [];

  /**
   * ### addPatch
   *
   * @description
   * Adds a patch to the collection for potential rollback
   *
   * @param patch - The patch to add
   */
  addPatch(patch: PatchCollection): void {
    this.patches.push(patch);
  }

  /**
   * ### rollback
   *
   * @description
   * Rolls back all collected patches in order. Only undoable patches are stored, so every
   * patch in the collection has a guaranteed undo method.
   */
  rollback(): void {
    this.patches.forEach((patch) => patch.undo());
  }
}

/**
 * ### OptimisticUpdaterOptions
 *
 * @description
 * Options for creating an OptimisticUpdater instance
 */
interface OptimisticUpdaterOptions<TApi> {
  /** Redux dispatch function (must support thunks) */
  dispatch: AppDispatch;
  /** The RTK Query API instance */
  api: TApi;
}

/**
 * ### UpdateOptions
 *
 * @description
 * Options for applying a single optimistic update
 */
interface UpdateOptions<TArgs, TData> {
  /** The endpoint name to update */
  endpointName: string;
  /** The query arguments */
  args: TArgs;
  /** Function to apply updates to the draft state */
  updateFn: (draft: TData) => void;
}

/**
 * ### UpsertDataOptions
 *
 * @description
 * Options for upserting data directly (without updateFn)
 */
interface UpsertDataOptions<TArgs, TData> {
  /** The endpoint name to update */
  endpointName: string;
  /** The query arguments */
  args: TArgs;
  /** The data to upsert directly */
  data: TData;
}

/**
 * ### OptimisticUpdater
 *
 * @description
 * A class that manages optimistic updates for RTK Query cache.
 * It encapsulates the manager, dispatch, and api to avoid repetitive parameter passing.
 * Generic over the API type for better type inference and safety.
 *
 * @example
 * const updater = new OptimisticUpdater({ dispatch, api });
 *
 * updater.update({
 *   endpointName: 'getProjectsByProjectIdSurveys',
 *   args: { projectId: 123 },
 *   updateFn: (draft) => { draft[0].status = 'ready'; }
 * });
 *
 * // For cache entries that might not exist, use upsertData with direct data:
 * updater.upsertData({
 *   endpointName: 'getShowLogicById',
 *   args: { id: '123' },
 *   data: newData
 * });
 *
 * // Later, on error:
 * updater.rollback();
 */
class OptimisticUpdater<
  TApi extends {
    util: {
      updateQueryData: (...args: never[]) => unknown;
      upsertQueryData: (...args: never[]) => unknown;
    };
  },
> {
  private manager: OptimisticUpdateManager;
  private dispatch: AppDispatch;
  private api: TApi;

  constructor(options: OptimisticUpdaterOptions<TApi>) {
    this.manager = new OptimisticUpdateManager();
    this.dispatch = options.dispatch;
    this.api = options.api;
  }

  /**
   * ### update
   *
   * @description
   * Applies an optimistic update to the cache and tracks it for potential rollback.
   * If the update fails (e.g., query doesn't exist in cache), it's silently skipped.
   *
   * @param options - The update options
   */
  update<TArgs, TData>(options: UpdateOptions<TArgs, TData>): void {
    const { endpointName, args, updateFn } = options;

    try {
      // RTK Query's updateQueryData is typed with string literal unions for endpoint names.
      // Since we're building a generic utility that works with dynamic endpoint names,
      // we cast to a signature that accepts any string endpoint name. This is safe because:
      // 1. RTK Query validates the endpoint name at runtime
      // 2. The API type parameter (TApi) ensures we're working with a valid API instance
      // 3. The generic TData type ensures type safety for the updateFn callback
      type UpdateQueryDataFn<T> = (
        endpointName: string,
        args: unknown,
        updateFn: (draft: T) => void,
      ) => ReturnType<TApi['util']['updateQueryData']>;

      const updateQueryData = this.api.util.updateQueryData as unknown as UpdateQueryDataFn<TData>;
      const thunk = updateQueryData(endpointName, args, updateFn);

      const dispatchThunk = this.dispatch as (action: unknown) => unknown;
      const result = dispatchThunk(thunk) as unknown;
      if (isUndoablePatch(result)) {
        this.manager.addPatch(result);
      }
    } catch (e) {
      // Cache entry doesn't exist yet; skip silently to preserve the optimistic-UX contract.
      // Log for diagnostic visibility if a real bug (endpoint typo, refactor drift,
      // non-serializable updateFn) is masquerading as a skip.
      console.warn(`[OptimisticUpdater] update skipped for ${endpointName}:`, e);
    }
  }

  /**
   * ### upsertData
   *
   * @description
   * Applies an optimistic update to the cache using upsertQueryData with direct data.
   * RTK Query's upsertQueryData is designed to accept direct data for insertion/replacement
   * operations. While it technically accepts a union type (direct data OR updateFn callback),
   * using direct data is the intended pattern for upsert operations. When updateFn callbacks
   * are used with non-existent cache entries, the function may be stored in Redux state
   * instead of being executed, leading to errors.
   * Tracks the update for potential rollback.
   *
   * @example
   * updater.upsertData({
   *   endpointName: 'getShowLogicById',
   *   args: { id: '123' },
   *   data: showLogicData
   * });
   *
   * @param options - The upsert options with direct data
   * @see https://redux-toolkit.js.org/rtk-query/usage/manual-cache-updates
   */
  upsertData<TArgs, TData>(options: UpsertDataOptions<TArgs, TData>): void {
    const { endpointName, args, data } = options;

    try {
      // RTK Query's upsertQueryData accepts a union type: CachedState | UpdateRecipe
      // (i.e., direct data OR an updateFn callback). However, we use direct data here
      // because that's the intended API design for upsert operations - it's meant for
      // inserting/replacing complete data. When updateFn callbacks are used with
      // non-existent cache entries, the function may be stored in state instead of
      // being executed, leading to errors. The type assertion is safe because:
      // 1. RTK Query's actual signature is: upsertQueryData(endpoint, args, data: T | (draft: T) => void)
      // 2. We're only using the direct data variant, which is a valid subset of the union
      // 3. The generic constraint ensures we're working with a valid RTK Query API instance
      // See: https://redux-toolkit.js.org/rtk-query/usage/manual-cache-updates
      type UpsertQueryDataFn<T> = (
        endpointName: string,
        args: unknown,
        data: T,
      ) => ReturnType<TApi['util']['upsertQueryData']>;

      const upsertQueryData = this.api.util.upsertQueryData as unknown as UpsertQueryDataFn<TData>;
      const thunk = upsertQueryData(endpointName, args, data);

      const dispatchThunk = this.dispatch as (action: unknown) => unknown;
      const result = dispatchThunk(thunk) as unknown;
      if (isUndoablePatch(result)) {
        this.manager.addPatch(result);
      }
    } catch (e) {
      // Should rarely fail; skip silently to preserve the optimistic-UX contract.
      // Log for diagnostic visibility if a real bug (endpoint typo, refactor drift,
      // non-serializable data shape) is masquerading as a skip.
      console.warn(`[OptimisticUpdater] upsertData skipped for ${endpointName}:`, e);
    }
  }

  /**
   * ### rollback
   *
   * @description
   * Rolls back all optimistic updates tracked by this updater
   */
  rollback(): void {
    this.manager.rollback();
  }
}

export default OptimisticUpdater;
